import React, { createContext, useContext, useState, useEffect } from 'react';
import { settingsAPI } from '../api/endpoints';

const ThemeContext = createContext(null);

const defaultTheme = {
  primary: '#14B8A6', // Teal-500
  primaryHover: '#0D9488', // Teal-600
  secondary: '#06B6D4', // Cyan-500
  secondaryHover: '#0891B2', // Cyan-600
  success: '#10B981', // Emerald-500
  successHover: '#059669', // Emerald-600
  danger: '#EF4444', // Red-500
  dangerHover: '#DC2626', // Red-600
  text: '#1F2937', // Gray-800
  textLight: '#6B7280', // Gray-500
  background: '#F9FAFB', // Gray-50
  buttonText: '#FFFFFF',
  buttonTextHover: '#FFFFFF'
};

export const ThemeProvider = ({ children }) => {
  const [theme, setTheme] = useState(defaultTheme);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadTheme();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadTheme = async () => {
    try {
      const response = await settingsAPI.getTheme();
      setTheme(response.data);
      applyThemeToDOM(response.data);
    } catch (error) {
      console.error('Failed to load theme:', error);
      applyThemeToDOM(defaultTheme);
    } finally {
      setLoading(false);
    }
  };

  const applyThemeToDOM = (themeColors) => {
    const root = document.documentElement;
    root.style.setProperty('--color-primary', themeColors.primary);
    root.style.setProperty('--color-primary-hover', themeColors.primaryHover);
    root.style.setProperty('--color-secondary', themeColors.secondary);
    root.style.setProperty('--color-secondary-hover', themeColors.secondaryHover);
    root.style.setProperty('--color-success', themeColors.success);
    root.style.setProperty('--color-success-hover', themeColors.successHover);
    root.style.setProperty('--color-danger', themeColors.danger);
    root.style.setProperty('--color-danger-hover', themeColors.dangerHover);
    root.style.setProperty('--color-text', themeColors.text);
    root.style.setProperty('--color-text-light', themeColors.textLight);
    root.style.setProperty('--color-background', themeColors.background);
    root.style.setProperty('--color-button-text', themeColors.buttonText);
    root.style.setProperty('--color-button-text-hover', themeColors.buttonTextHover);
  };

  const updateTheme = async (newTheme, scope = 'user') => {
    try {
      const response = await settingsAPI.updateTheme(newTheme, scope);
      setTheme(response.data.theme);
      applyThemeToDOM(response.data.theme);
      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to update theme'
      };
    }
  };

  const resetTheme = async (scope = 'user') => {
    try {
      const response = await settingsAPI.resetTheme(scope);
      setTheme(response.data.theme);
      applyThemeToDOM(response.data.theme);
      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to reset theme'
      };
    }
  };

  const value = {
    theme,
    loading,
    updateTheme,
    resetTheme,
    reloadTheme: loadTheme
  };

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};
